import { User } from '@supabase/supabase-js';
import Constants from 'expo-constants';
import { Platform } from 'react-native';
import { logger } from '../lib/logger';
import type { WelcomeConversion } from '../lib/purchaseWelcome';

/** localStorage key prefix (+ order ID): same as ConversionTracking.php on WordPress. */
const CONVERSION_STORAGE_PREFIX = 'osj_conv_';

const GA_MEASUREMENT_ID = process.env.EXPO_PUBLIC_GA_MEASUREMENT_ID;
const GTM_CONTAINER_ID = process.env.EXPO_PUBLIC_GTM_CONTAINER_ID;

declare global {
  interface Window {
    gtag: (
      command: 'config' | 'event' | 'js' | 'set',
      targetId: string | Date,
      config?: Record<string, any>
    ) => void;
    dataLayer: any[];
    _dcq: any[]; // Required for Drip tracking
  }
}

class GoogleAnalyticsService {
  private isInitialized = false;
  
  private getDebugMode(): boolean {
    return __DEV__ || Constants.expoConfig?.extra?.appVariant !== 'production';
  }

  private getBaseEventParams(): Record<string, any> {
    return {
      app_name: 'josephine',
      app_variant: Constants.expoConfig?.extra?.appVariant || 'dev',
      debug_mode: this.getDebugMode(),
    };
  }

  //Initializes GA4 and GTM scripts for web platform
  initialize(): void {
    if (Platform.OS !== 'web' || typeof window === 'undefined' || this.isInitialized) return;
    
    if (!GA_MEASUREMENT_ID) {
      logger.warn('GA Measurement ID not configured. Skipping GA initialization.');
      return;
    }

    const existingScript = document.getElementById('google-analytics-js');
    if (existingScript) {
      this.isInitialized = true;
      return;
    }

    try {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function(...args: any[]) { 
        window.dataLayer.push(args); 
      };
      window.gtag('js', new Date());
      
      // Initial GA4 configuration
      window.gtag('config', GA_MEASUREMENT_ID, {
        send_page_view: false,
        debug_mode: this.getDebugMode(),
      });

      // Load Google Analytics script
      const gaScript = document.createElement('script');
      gaScript.id = 'google-analytics-js';
      gaScript.async = true;
      gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      document.head.appendChild(gaScript);

      // Load Google Tag Manager script
      if (GTM_CONTAINER_ID) {
        const gtmScript = document.createElement('script');
        gtmScript.id = 'gtm-js';
        gtmScript.async = true;
        gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`;
        document.head.appendChild(gtmScript);
      }

      this.isInitialized = true;
    } catch (error) {
      logger.error('GA Init Failed:', error);
    }
  }

  /**
   * GA4-shaped `purchase` for Google Tag Manager (same container as the
   * WordPress site). Fired from the post-purchase welcome page, which replaced
   * the WordPress thank-you page where GTM used to see the order; tags that
   * keyed on that page (Podscribe purchase, GA4 purchase) listen for this
   * dataLayer event instead.
   */
  trackPurchase(purchase: {
    transactionId: string;
    value: number;
    currency: string;
    items: { id: string; name: string; price: number; quantity: number; category?: string }[];
  }): void {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;

    try {
      window.dataLayer = window.dataLayer || [];
      // Clear the previous ecommerce object so GTM never merges two orders.
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'purchase',
        ...this.getBaseEventParams(),
        ecommerce: {
          transaction_id: purchase.transactionId,
          value: purchase.value,
          currency: purchase.currency,
          items: purchase.items.map((item) => ({
            item_id: item.id,
            item_name: item.name,
            item_category: item.category,
            price: item.price,
            quantity: item.quantity,
          })),
        },
      });
    } catch (error) {
      logger.error('GA Purchase Failed:', error);
    }
  }

  /**
   * `os_order_complete`: the conversion event the Google Ads (and Podscribe)
   * tags in GTM are built on, in the exact shape WordPress thank-you pages
   * push (ConversionTracking.php in openstudio-core). One push per paid
   * order, behind a localStorage flag so a reload never counts twice; Google
   * Ads also dedupes on transaction_id. The buyer's email, used for enhanced
   * conversions, comes from the session, never from the URL payload.
   */
  trackOrderComplete(conversion: WelcomeConversion, email: string): void {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;

    const key = `${CONVERSION_STORAGE_PREFIX}${conversion.order_id}`;
    try {
      if (window.localStorage.getItem(key)) return;
      window.localStorage.setItem(key, '1');
    } catch {
      // Private mode / blocked storage: push anyway.
    }

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        ...conversion,
        event: 'os_order_complete',
        user_data: { email: email.trim().toLowerCase() },
      });
    } catch (error) {
      logger.error('GA order complete failed:', error);
    }
  }

  //Tracks manual page views
  trackPageView(pagePath: string, pageTitle?: string): void {
    if (Platform.OS !== 'web' || typeof window === 'undefined' || !window.gtag) return;

    try {
      const finalTitle = pageTitle || document.title || 'Josephine';
      
      window.gtag('set', {
        page_path: pagePath,
        page_title: finalTitle,
        page_location: window.location.href,
      } as any);

      window.gtag('event', 'page_view', {
        ...this.getBaseEventParams(),
      });
    } catch (error) {
      logger.error('GA PageView Failed:', error);
    }
  }

  //Specific tracking functions for courses and lessons
  trackLessonView(courseSlug: string, lessonSlug: string, courseTitle?: string, lessonTitle?: string, user?: User | null): void {
    this.sendEvent('view_lesson', {
      course_slug: courseSlug,
      lesson_slug: lessonSlug,
      course_title: courseTitle,
      lesson_title: lessonTitle,
      user_id: user?.id,
    });
  }

  trackCourseView(courseSlug: string, courseTitle?: string, user?: User | null): void {
    this.sendEvent('view_course', {
      course_slug: courseSlug,
      course_title: courseTitle,
      user_id: user?.id,
    });
  }

  trackCourseBegin(courseSlug: string, courseTitle?: string, user?: User | null): void {
    this.sendEvent('course_begin', {
      course_slug: courseSlug,
      course_title: courseTitle,
      user_id: user?.id,
    });
  }

  trackLessonBegin(courseSlug: string, lessonSlug: string, courseTitle?: string, lessonTitle?: string, user?: User | null): void {
    this.sendEvent('lesson_begin', {
      course_slug: courseSlug,
      lesson_slug: lessonSlug,
      course_title: courseTitle,
      lesson_title: lessonTitle,
      user_id: user?.id,
    });
  }

  // Identifies the user in GA4 and Drip
  setUserProperties(user: User | null): void {
    if (Platform.OS !== 'web' || typeof window === 'undefined' || !window.gtag || !GA_MEASUREMENT_ID) return;
    if (user) {
      // Set user ID for GA4
      window.gtag('config', GA_MEASUREMENT_ID, { 'user_id': user.id });
      window.gtag('set', 'user_properties', { user_id: user.id });

      // Identify user for Drip tracking (Email is primary identifier)
      if (user.email) {
        // User identification sent to Drip
        window._dcq = window._dcq || [];
        window._dcq.push(['identify', { 
          email: user.email,
          user_id: user.id 
        }]);
      }
    }
  }

  //Internal helper to dispatch gtag events
  private sendEvent(name: string, params: Record<string, any>): void {
    if (Platform.OS !== 'web' || typeof window === 'undefined' || !window.gtag) return;
    window.gtag('event', name, { ...params, ...this.getBaseEventParams() });
  }
}

export const gaService = new GoogleAnalyticsService();